"use client";

import {
  type RazorpayProviderError,
  readProviderError,
} from "@/lib/razorpay-custom-checkout";
import { loadRazorpaySdkScript } from "@/lib/razorpay-script-loader";

export type RazorpaySuccessResponse = {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_subscription_id?: string;
  razorpay_signature: string;
};

export type RazorpayCheckoutOptions = {
  key: string;
  amount?: number;
  currency?: string;
  name: string;
  description?: string;
  order_id?: string;
  subscription_id?: string;
  callback_url?: string;
  redirect?: boolean;
  timeout?: number;
  customer_id?: string;
  remember_customer?: boolean;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
    method?: string;
    vpa?: string;
    bank?: string;
    wallet?: string;
  };
  retry?: {
    enabled?: boolean;
    max_count?: number;
  };
  readonly?: {
    name?: boolean;
    email?: boolean;
    contact?: boolean;
  };
  hidden?: {
    contact?: boolean;
    email?: boolean;
  };
  notes?: Record<string, string>;
  webview_intent?: boolean;
  config?: {
    display: {
      blocks: Record<
        string,
        {
          name: string;
          instruments: Array<{
            method:
              | "card"
              | "upi"
              | "netbanking"
              | "wallet"
              | "emi"
              | "paylater"
              | "cardless_emi";
          }>;
        }
      >;
      sequence: string[];
      preferences?: {
        show_default_blocks?: boolean;
      };
    };
  };
  modal?: {
    ondismiss?: () => void;
    confirm_close?: boolean;
  };
  theme?: {
    color?: string;
  };
};

type RazorpayInstance = {
  open: () => void;
  on?: (event: string, handler: (payload: unknown) => void) => void;
};

type RazorpayFailurePayload = {
  error?: {
    description?: string;
    reason?: string;
    code?: string;
  };
};

declare global {
  interface Window {
    Razorpay?: new (
      options: RazorpayCheckoutOptions & {
        handler: (response: RazorpaySuccessResponse) => void;
      },
    ) => RazorpayInstance;
  }
}

let razorpayLoadPromise: Promise<void> | null = null;
const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const RAZORPAY_SCRIPT_STATUS_ATTR = "data-razorpay-checkout-status";
const RAZORPAY_SCRIPT_TIMEOUT_MS = 15000;

const toCheckoutErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
};

const toPaymentFailureMessage = (payload: unknown): string => {
  const failure = payload as RazorpayFailurePayload;
  const description = failure.error?.description?.trim();
  if (description) return description;

  const reason = failure.error?.reason?.trim();
  if (reason) return reason.replaceAll("_", " ");

  const code = failure.error?.code?.trim();
  if (code) return code.replaceAll("_", " ");

  return "Payment failed. Please try again.";
};

export async function loadRazorpayCheckout(): Promise<
  { ok: true; data: { loaded: true } } | { ok: false; message: string }
> {
  // Custom Checkout and Standard Checkout both publish `window.Razorpay`.
  // A resolved promise only proves Standard owned the global at that moment;
  // switching to Collect can replace it later in the same checkout session.
  if (
    typeof window !== "undefined" &&
    window.__hiiveRazorpaySdk &&
    window.__hiiveRazorpaySdk !== "checkout"
  ) {
    razorpayLoadPromise = null;
  }

  if (!razorpayLoadPromise) {
    razorpayLoadPromise = loadRazorpaySdkScript({
      src: RAZORPAY_SCRIPT_SRC,
      sdk: "checkout",
      statusAttribute: RAZORPAY_SCRIPT_STATUS_ATTR,
      isReady: () => Boolean(window.Razorpay),
      timeoutMs: RAZORPAY_SCRIPT_TIMEOUT_MS,
      browserMessage: "Razorpay can only be loaded in the browser.",
    }).then((result) => {
      if (!result.ok) {
        throw new Error(result.message);
      }
    });
  }

  try {
    await razorpayLoadPromise;
  } catch (error) {
    razorpayLoadPromise = null;
    return {
      ok: false,
      message:
        error instanceof Error
          ? error.message
          : "Failed to load payment provider.",
    };
  }

  if (!window.Razorpay) {
    return { ok: false, message: "Payment provider did not initialize." };
  }

  return { ok: true, data: { loaded: true } };
}

export async function openRazorpayCheckout(
  options: RazorpayCheckoutOptions,
  onProviderFailure?: (failure: {
    message: string;
    providerError: RazorpayProviderError;
  }) => void,
): Promise<
  | { ok: true; data: RazorpaySuccessResponse }
  | {
      ok: false;
      message: string;
      /**
       * The provider's own account of the refusal, when it gave one.
       *
       * This exists because of one specific, common failure. Razorpay's mobile
       * Intent screen lists the top PSP apps irrespective of whether they are
       * installed, and tapping one you do not have emits
       * `reason: "intent_no_apps_error"`. Collapsing that into a display string
       * is how "no UPI app on this device" and "the payer changed their mind"
       * became the same event — unmeasurable, and impossible to recover from
       * differently. Absent for our own dismissals, which are not its verdict.
       */
      providerError?: RazorpayProviderError;
    }
> {
  const load = await loadRazorpayCheckout();
  if (!load.ok) return load;

  if (!window.Razorpay) {
    return { ok: false, message: "Payment provider is unavailable." };
  }

  const RazorpayCtor = window.Razorpay;

  return await new Promise((resolve) => {
    let settled = false;
    const settle = (
      result:
        | { ok: true; data: RazorpaySuccessResponse }
        | {
            ok: false;
            message: string;
            providerError?: RazorpayProviderError;
          },
    ) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("pagehide", onPageHide);
      resolve(result);
    };

    /**
     * Redirect mode never calls `handler` — it form-POSTs to `callback_url` and
     * the document dies. Without this, `ondismiss` fires on the way out and the
     * caller is told "Payment was not completed" about a payment that is in fact
     * on its way to the bank.
     */
    const onPageHide = () => {
      settled = true;
    };
    if (options.redirect) {
      window.addEventListener("pagehide", onPageHide);
    }

    /**
     * The last thing the provider said before the payer closed the modal.
     *
     * With retry enabled, a failed app handoff leaves the person looking at
     * Razorpay's dead end and their only move is to dismiss it — so the
     * dismissal is where the real cause has to be reported, or it is lost.
     */
    let lastProviderError: RazorpayProviderError | undefined;
    let lastProviderMessage: string | undefined;

    let instance: RazorpayInstance;

    try {
      instance = new RazorpayCtor({
        ...options,
        modal: {
          ...options.modal,
          ondismiss: () => {
            options.modal?.ondismiss?.();
            settle({
              ok: false,
              message: lastProviderMessage ?? "Payment was not completed.",
              ...(lastProviderError
                ? { providerError: lastProviderError }
                : {}),
            });
          },
        },
        handler: (response) => settle({ ok: true, data: response }),
      });
    } catch (error) {
      settle({
        ok: false,
        message: toCheckoutErrorMessage(
          error,
          "Could not open payment screen. Please try again.",
        ),
      });
      return;
    }

    /**
     * Always read the failure, even when Razorpay will offer its own retry.
     *
     * The old guard only settled when retry was disabled, so on the Intent path
     * — which enabled retry — this handler did nothing at all: the payload was
     * parsed by nobody and the only trace of a failed app handoff was the
     * generic "Payment was not completed." the dismissal produced minutes
     * later. Reading it is unconditional now; whether it *ends* the checkout
     * still is, because settling under Razorpay's own retry screen would
     * abandon a payment the payer is being invited to reattempt.
     */
    instance.on?.("payment.failed", (payload) => {
      const { message, providerError } = readProviderError(payload);
      // Observe every decline, including retries followed by success in this
      // same modal. Analytics must never interrupt provider settlement.
      try {
        onProviderFailure?.({ message, providerError });
      } catch {}
      lastProviderError = providerError;
      lastProviderMessage = message;
      if (options.retry?.enabled === false) {
        settle({
          ok: false,
          message: toPaymentFailureMessage(payload),
          providerError,
        });
      }
    });

    try {
      instance.open();
    } catch (error) {
      settle({
        ok: false,
        message: toCheckoutErrorMessage(
          error,
          "Could not open payment screen. Please try again.",
        ),
      });
    }
  });
}
